import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

const MAX_SEARCH_QUERY_LENGTH = 500;

export interface SearchHistoryItem {
  query: string;
  searched_at: string;
}

interface SearchHistoryResponse {
  searches?: SearchHistoryItem[];
}

function normalizeHistoryQueryValue(query: string): string {
  return query
    .normalize('NFC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function useSearchHistory() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  return useQuery({
    queryKey: queryKeys.user.searchHistory(userId ?? ''),
    enabled: !!userId && !!token,
    queryFn: async ({ signal }) => {
      const response = await apiFetch<SearchHistoryResponse>('/user/search-history', {
        token: token!,
        signal,
      });
      return response.searches || [];
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useSaveSearchHistoryMutation() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (query: string) => {
      const trimmed = query.trim();
      if (!token) throw new Error('Not authenticated');
      if (trimmed.length < 2) return;
      if (trimmed.length > MAX_SEARCH_QUERY_LENGTH) return;
      await apiFetch('/user/search-history', {
        token,
        method: 'POST',
        body: { query: trimmed },
      });
    },
    onMutate: async (query: string) => {
      if (!userId) return;
      const trimmed = query.trim();
      if (trimmed.length < 2) return;
      if (trimmed.length > MAX_SEARCH_QUERY_LENGTH) return;

      const queryKey = queryKeys.user.searchHistory(userId);
      await queryClient.cancelQueries({ queryKey });
      const previousSearches = queryClient.getQueryData<SearchHistoryItem[]>(queryKey);

      queryClient.setQueryData<SearchHistoryItem[]>(queryKey, (old) => {
        const existing = old ?? [];
        const normalized = normalizeHistoryQueryValue(trimmed);
        const filtered = existing.filter(
          (s) => normalizeHistoryQueryValue(s.query) !== normalized
        );
        return [{ query: trimmed, searched_at: new Date().toISOString() }, ...filtered].slice(0, 5);
      });

      return { previousSearches };
    },
    onError: (_err, _query, context) => {
      if (context?.previousSearches !== undefined && userId) {
        queryClient.setQueryData(queryKeys.user.searchHistory(userId), context.previousSearches);
      }
    },
  });
}

export function useClearSearchHistoryMutation() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('Not authenticated');
      await apiFetch('/user/search-history', {
        token,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      if (userId) {
        queryClient.setQueryData<SearchHistoryItem[]>(queryKeys.user.searchHistory(userId), []);
      }
    },
  });
}
